<nav class="bg-white shadow fixed top-0 left-0 right-0 z-50">
  <div class="px-4 sm:px-6 lg:px-6">
    <div class="flex justify-between h-16">
      <!-- Left: Logo + Desktop nav links -->
      <div class="flex">
        <div class="flex-shrink-0 flex items-center">
          <a routerLink="/" class="flex items-center text-xl font-bold" style="color: var(--primary-color)">
            <img src="bookletics-favicon.png" alt="Bookletics" class="h-8 w-8 mr-2" width="32" height="32">
            Bookletics
          </a>
        </div>
        <!-- Desktop navigation links -->
        <div class="hidden sm:ml-6 sm:flex sm:space-x-8">
          @for (link of navigationLinks; track link.path) {
            <a [routerLink]="link.path"
               routerLinkActive="nav-link-active"
               [routerLinkActiveOptions]="{exact: link.exact || false}"
               class="inline-flex items-center self-center px-3 py-1.5 text-sm font-medium transition-colors nav-link"
               style="border-radius: 8px;">
              {{link.title}}
            </a>
          }
        </div>
      </div>

      <!-- Right: Desktop user area -->
      <div class="hidden sm:ml-6 sm:flex sm:items-center">
        @if (currentUser$ | async; as user) {
          <div class="relative">
            <!-- Avatar trigger (fades out when panel open) -->
            <button type="button" (click)="toggleMenu()" class="user-avatar-btn"
              [class.avatar-hidden]="isMenuOpen"
              id="user-menu-button" [attr.aria-expanded]="isMenuOpen" aria-haspopup="true">
              <span class="sr-only">Benutzermenü öffnen</span>
              <div class="user-avatar">{{ getUserInitial(user) }}</div>
            </button>

            <!-- Desktop panel (always in DOM, animated via CSS) -->
            <div class="desktop-menu" [class.open]="isMenuOpen" [attr.inert]="!isMenuOpen || null" [attr.aria-hidden]="!isMenuOpen" role="dialog" aria-label="Benutzermenü">
              <!-- Header: avatar + name/email + X -->
              <div class="desktop-menu-header">
                <div class="user-avatar-lg">{{ getUserInitial(user) }}</div>
                <div class="dropdown-user-details">
                  <div class="dropdown-user-name">{{ user?.username || 'User' }}</div>
                  <div class="dropdown-user-email">{{ user?.email || '' }}</div>
                </div>
                <button type="button" class="desktop-close-btn" (click)="toggleMenu()" aria-label="Menü schließen">
                  <app-icon [type]="IconType.X" [size]="18"></app-icon>
                </button>
              </div>

              <!-- Body -->
              <div class="desktop-menu-body">
                <!-- Organization -->
                @if (organizationService.availableOrganizations().length > 0 && selectedOrganization) {
                  <div class="dropdown-section">
                    <div class="section-label">ORGANISATION</div>
                    <button type="button" class="org-selector"
                      (click)="toggleOrgDropdown($event)"
                      [disabled]="organizationService.availableOrganizations().length <= 1">
                      <div class="org-avatar">{{ organizationService.getOrgInitials(selectedOrganization) }}</div>
                      <span class="org-name">{{ selectedOrganization.name }}</span>
                      @if (organizationService.availableOrganizations().length > 1) {
                        <app-icon [type]="IconType.CHEVRON_DOWN" [size]="16" class="org-chevron" [class.rotate-180]="isOrgDropdownOpen"></app-icon>
                      }
                    </button>
                    @if (isOrgDropdownOpen) {
                      <div class="org-options">
                        @for (org of organizationService.availableOrganizations(); track org.id) {
                          <button type="button" class="org-option-item"
                            (click)="selectOrganization(org.id, $event)"
                            [class.selected]="org.id === organizationService.selectedOrganizationId()">
                            <div class="org-avatar-sm">{{ organizationService.getOrgInitials(org) }}</div>
                            <span>{{ org.name }}</span>
                            @if (org.id === organizationService.selectedOrganizationId()) {
                              <app-icon [type]="IconType.CHECK" [size]="16" class="org-check"></app-icon>
                            }
                          </button>
                        }
                      </div>
                    }
                  </div>
                }

                <div class="dropdown-divider"></div>

                <!-- Account links -->
                <div class="dropdown-menu-section">
                  @for (link of accountLinks; track link.title) {
                    <a [routerLink]="link.path" class="dropdown-menu-item" (click)="isMenuOpen = false">
                      <app-icon [type]="link.icon" [size]="18" class="menu-item-icon"></app-icon>
                      <span>{{ link.title }}</span>
                    </a>
                  }
                </div>

                <div class="dropdown-divider"></div>

                <!-- Logout -->
                <div class="dropdown-menu-section">
                  <button (click)="logout()" class="dropdown-menu-item logout-item">
                    <app-icon [type]="IconType.LOG_OUT" [size]="18" class="menu-item-icon"></app-icon>
                    <span>Abmelden</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        } @else {
          <!-- Desktop auth links -->
          <div class="auth-button-group">
            @for (link of authLinks; track link.path + (link.queryParams?.['mode'] || '')) {
              <a [routerLink]="link.path"
                 [queryParams]="link.queryParams"
                 [class]="link.title === 'Einloggen' ? 'btn btn-primary' : 'btn btn-secondary'">
                {{link.title}}
              </a>
            }
          </div>
        }
      </div>

      <!-- Mobile hamburger / X toggle button -->
      <div class="flex items-center sm:hidden">
        <button type="button" (click)="toggleMenu()"
          class="mobile-menu-toggle"
          aria-controls="mobile-menu" [attr.aria-expanded]="isMenuOpen">
          <span class="sr-only">{{ isMenuOpen ? 'Menü schließen' : 'Hauptmenü öffnen' }}</span>
          <div class="menu-icon-wrapper" [class.open]="isMenuOpen">
            <svg [class.hidden]="isMenuOpen" class="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <svg [class.hidden]="!isMenuOpen" class="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        </button>
      </div>
    </div>
  </div>
</nav>

<!-- Mobile full-screen panel (below nav bar) -->
<div class="mobile-panel" [class.open]="isMenuOpen" [attr.inert]="!isMenuOpen || null" [attr.aria-hidden]="!isMenuOpen" id="mobile-menu">
  @if (currentUser$ | async; as user) {
    <!-- User info -->
    <div class="mobile-user-info">
      <div class="user-avatar-lg">{{ getUserInitial(user) }}</div>
      <div class="mobile-user-details">
        <div class="mobile-user-name">{{ user?.username || 'User' }}</div>
        <div class="mobile-user-email">{{ user?.email || '' }}</div>
      </div>
      @if (isAdmin) {
        <span class="admin-badge">
          <span class="admin-badge-dot"></span>
          ADMIN
        </span>
      }
    </div>

    <!-- Organization -->
    @if (organizationService.availableOrganizations().length > 0 && selectedOrganization) {
      <div class="mobile-section">
        <div class="section-label">ORGANISATION</div>
        <button type="button" class="org-selector"
          (click)="toggleOrgDropdown($event)"
          [disabled]="organizationService.availableOrganizations().length <= 1">
          <div class="org-avatar">{{ organizationService.getOrgInitials(selectedOrganization) }}</div>
          <span class="org-name">{{ selectedOrganization.name }}</span>
          @if (organizationService.availableOrganizations().length > 1) {
            <app-icon [type]="IconType.CHEVRON_DOWN" [size]="16" class="org-chevron" [class.rotate-180]="isOrgDropdownOpen"></app-icon>
          }
        </button>
        @if (isOrgDropdownOpen) {
          <div class="org-options">
            @for (org of organizationService.availableOrganizations(); track org.id) {
              <button type="button" class="org-option-item"
                (click)="selectOrganization(org.id, $event)"
                [class.selected]="org.id === organizationService.selectedOrganizationId()">
                <div class="org-avatar-sm">{{ organizationService.getOrgInitials(org) }}</div>
                <span>{{ org.name }}</span>
                @if (org.id === organizationService.selectedOrganizationId()) {
                  <app-icon [type]="IconType.CHECK" [size]="16" class="org-check"></app-icon>
                }
              </button>
            }
          </div>
        }
      </div>
    }

    <div class="mobile-divider"></div>

    <!-- Navigation links -->
    <div class="mobile-nav-section">
      @for (link of navigationLinks; track link.path) {
        @if (!link.isAdminMenu) {
          <a [routerLink]="link.path"
             routerLinkActive="mobile-nav-active"
             [routerLinkActiveOptions]="{exact: link.exact || false}"
             class="mobile-nav-item"
             (click)="isMenuOpen = false">
            @if (link.icon) {
              <app-icon [type]="link.icon" [size]="20" class="menu-item-icon"></app-icon>
            }
            <span>{{ link.title }}</span>
          </a>
        }
      }
    </div>

    <!-- Account section -->
    <div class="mobile-section">
      <div class="section-label">ACCOUNT</div>
      <div class="mobile-account-items">
        @for (link of accountLinks; track link.title) {
          <a [routerLink]="link.path" class="mobile-nav-item" (click)="isMenuOpen = false">
            <app-icon [type]="link.icon" [size]="20" class="menu-item-icon"></app-icon>
            <span>{{ link.title }}</span>
          </a>
        }
      </div>
    </div>

    <!-- Admin section -->
    @if (isAdmin) {
      <div class="mobile-nav-section">
        <button type="button" class="mobile-nav-item admin-toggle"
          (click)="toggleAdminSubmenu($event)"
          [attr.aria-expanded]="isAdminExpanded"
          aria-controls="mobile-admin-submenu">
          <app-icon [type]="IconType.SETTINGS" [size]="20" class="menu-item-icon"></app-icon>
          <span>Admin</span>
          <app-icon [type]="IconType.CHEVRON_DOWN" [size]="16" class="admin-chevron-icon" [class.rotate-180]="isAdminExpanded"></app-icon>
        </button>
        @if (isAdminExpanded) {
          <nav id="mobile-admin-submenu" class="admin-submenu-panel" aria-label="Admin-Unternavigation">
            @for (subItem of adminNavItems; track subItem.routerLink) {
              <a [routerLink]="subItem.routerLink"
                 routerLinkActive="mobile-nav-active"
                 class="mobile-nav-item submenu-item"
                 (click)="isMenuOpen = false">
                <app-icon [type]="subItem.icon" [size]="16" class="menu-item-icon"></app-icon>
                <span>{{ subItem.title }}</span>
              </a>
            }
          </nav>
        }
      </div>
    }

    <div class="mobile-divider"></div>

    <!-- Logout -->
    <div class="mobile-nav-section">
      <button (click)="logout()" class="mobile-nav-item logout-item">
        <app-icon [type]="IconType.LOG_OUT" [size]="20" class="menu-item-icon"></app-icon>
        <span>Abmelden</span>
      </button>
    </div>

  } @else {
    <!-- Not logged in -->
    <div class="mobile-nav-section">
      @for (link of navigationLinks; track link.path) {
        <a [routerLink]="link.path"
           routerLinkActive="mobile-nav-active"
           [routerLinkActiveOptions]="{exact: link.exact || false}"
           class="mobile-nav-item"
           (click)="isMenuOpen = false">
          @if (link.icon) {
            <app-icon [type]="link.icon" [size]="20" class="menu-item-icon"></app-icon>
          }
          <span>{{ link.title }}</span>
        </a>
      }
    </div>
    <div class="mobile-divider"></div>
    <div class="mobile-auth-section">
      @for (link of authLinks; track link.path + (link.queryParams?.['mode'] || '')) {
        <a [routerLink]="link.path"
           [queryParams]="link.queryParams"
           [class]="link.title === 'Registrieren' ? 'btn btn-primary btn-full' : 'btn btn-secondary btn-full'"
           (click)="isMenuOpen = false">
          {{link.title}}
        </a>
      }
    </div>
  }
</div>
